import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { debounce } from "lodash";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { toast } from "react-hot-toast";
import {
  MagnifyingGlassIcon,
  XMarkIcon,
  CheckIcon,
  PlusIcon,
  ArrowLeftIcon,
  ChevronDownIcon,
} from "@heroicons/react/24/outline";
import axios from "../../../api/axios";
import { mapErrors } from "../../Utils";
import { SelectorListSkeleton } from "../ResourceLoading";
import { PMField } from "../PMField";
import { PMDialog } from "../PMDialog";
import { useApiCache } from "../../../hooks/useApiCache";
import { ServiceReportTypeConditionsDialog } from "../../dashboard/service/settings/service-report-type-conditions-dialog";
import { useAutofocusOnOpen } from "../useAutofocusOnOpen";

const FETCH_URL = "/service/settings/report_types/";

export const ReportTypeSelector = (props) => {
  const {
    setFieldValue,
    values,
    errors,
    onChange,
    module,
    module_param,
    fields,
  } = props;

  const name = props?.name ?? "report_type";
  const label = "Report Type";

  const { t } = useTranslation();
  const { getCachedData, setCachedData } = useApiCache();

  // ── Dialog state ──────────────────────────────────────────────────────────
  const [open, setOpen] = useState(false);
  const [inCreateView, setInCreateView] = useState(false);
  const [conditionsDialogOpen, setConditionsDialogOpen] = useState(false);

  // ── Search & list state ───────────────────────────────────────────────────
  const [query, setQuery] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const searchInputRef = useAutofocusOnOpen(open && !inCreateView);

  // ── Selected state ────────────────────────────────────────────────────────
  const [selected, setSelected] = useState({
    id: null,
    field1: "Ingen valgt",
    field2: null,
  });

  useEffect(() => {
    if (props?.selected?.id) {
      setSelected({
        id: props.selected.id,
        field1: props.selected.displayTitle ?? "Ingen valgt",
      });
    } else {
      setSelected({ id: null, field1: "Ingen valgt", field2: null });
    }
  }, [props.selected]);

  // ── Open/close ────────────────────────────────────────────────────────────
  const handleOpen = () => {
    if (props.disabled) return;
    setOpen(true);
    setInCreateView(false);
  };

  const handleClose = () => {
    setOpen(false);
    setInCreateView(false);
    setQuery("");
  };

  // ── Propagate selection ───────────────────────────────────────────────────
  const applySelection = (id) => {
    if (values) setFieldValue(name, id);
    else onChange({ target: { name, value: id } });
  };

  const handleSelect = (item) => {
    setSelected({
      id: item.id,
      field1: item.primary ?? "Ingen valgt",
      field2: item.secondary ?? null,
    });
    applySelection(item.id);
    handleClose();
  };

  const handleSelectFromForm = (data) => {
    setSelected({
      id: data?.id,
      field1: data?.displayTitle ?? data?.title ?? "Ingen valgt",
    });
    applySelection(data?.id);
    handleClose();
  };

  // ── Data fetching (with cache) ────────────────────────────────────────────
  const fetchItems = useCallback(
    async (searchQuery) => {
      const cacheKey = `report_types_${searchQuery || "default"}`;
      const cached = getCachedData(cacheKey);

      if (cached) {
        const array = [
          { id: null, value: null, primary: "Ingen valgt", secondary: null },
        ];
        cached.forEach((row) =>
          array.push({ id: row.id, value: row.id, primary: row.title, secondary: row.description })
        );
        setItems(array);
        return;
      }

      setLoading(true);
      try {
        const response = await axios({
          method: "get",
          url: FETCH_URL,
          params: {
            query: searchQuery || undefined,
            page: 1,
            sortBy: "created_at",
            sort: "desc",
          },
        });

        if (response.data?.data) {
          setCachedData(cacheKey, response.data.data);
          const array = [
            { id: null, value: null, primary: "Ingen valgt", secondary: null },
          ];
          response.data.data.forEach((row) =>
            array.push({ id: row.id, value: row.id, primary: row.title, secondary: row.description })
          );
          setItems(array);
        }
      } catch {
        setItems([
          { id: null, value: null, primary: "Ingen valgt", secondary: null },
        ]);
      } finally {
        setLoading(false);
      }
    },
    [getCachedData, setCachedData]
  );

  const debouncedFetch = useCallback(
    debounce((q) => fetchItems(q), 300),
    [fetchItems]
  );

  useEffect(() => {
    if (!open) return;
    debouncedFetch(query);
    return () => debouncedFetch.cancel();
  }, [query, open, debouncedFetch]);

  // ── Create form ───────────────────────────────────────────────────────────
  const createOrUpdate = (vals) => {
    const data = { ...vals, module, module_param };
    return axios.post(FETCH_URL, data).then((r) => r);
  };

  const renderCreateForm = () => (
    <Formik
      initialValues={{
        id: null,
        serial_number: "",
        placement: "",
        installation_year: "",
        production_year: "",
        submit: null,
      }}
      validationSchema={Yup.object({
        system_type_id: Yup.number().required("Required"),
        production_year: Yup.string(),
        installation_year: Yup.string(),
        serial_number: Yup.string(),
      })}
      onSubmit={(vals, helpers) => {
        try {
          toast.promise(createOrUpdate(vals), {
            loading: t("Saving...."),
            success: (data) => {
              if (data.status === 500 || data.status === 400)
                throw new Error("Server error");
              if (data.data.errors) throw new Error(mapErrors(data.data.errors));
              fetchItems(query);
              if (data?.data?.id) handleSelectFromForm(data.data);
              return t("Data was saved!");
            },
            error: () => t("Something went wrong. Could not save data!"),
          });
          helpers.setStatus({ success: true });
          helpers.setSubmitting(false);
        } catch (err) {
          toast.error("Something went wrong...");
          helpers.setStatus({ success: false });
          helpers.setErrors({ submit: err.message });
          helpers.setSubmitting(false);
        }
      }}
    >
      {(formik) => (
        <Form className="space-y-4 p-4">
          <PMField name="serial_number" type="text" label={t("Serial Number")} {...formik} />
          <PMField name="placement" type="text" label={t("Placement")} {...formik} />
          <PMField name="installation_year" type="number" label={t("Installation Year")} {...formik} />
          <PMField name="production_year" type="number" label={t("Production Year")} {...formik} />
          <div className="pt-2">
            <button
              type="button"
              onClick={formik.submitForm}
              disabled={formik.isSubmitting}
              className="w-full inline-flex justify-center rounded-md bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-50"
            >
              {t("create")}
            </button>
          </div>
        </Form>
      )}
    </Formik>
  );

  // ── Trigger button ────────────────────────────────────────────────────────
  const hasSelection = selected?.id != null;

  return (
    <>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {hasSelection ? (
            <button
              type="button"
              onClick={() => setConditionsDialogOpen(true)}
              className="text-blue-500 hover:text-blue-700"
            >
              {t(label)}
            </button>
          ) : (
            t(label)
          )}
        </label>
        <button
          type="button"
          disabled={props.disabled}
          onClick={handleOpen}
          className={[
            "relative w-full flex items-center justify-between",
            "rounded-md border bg-white px-3 py-2 text-sm shadow-sm",
            "transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500",
            props.disabled
              ? "cursor-not-allowed border-gray-200 text-gray-400"
              : hasSelection
              ? "border-blue-400 text-gray-900 hover:border-blue-500"
              : "border-gray-300 text-gray-500 hover:border-gray-400",
          ].join(" ")}
        >
          <div className="min-w-0">
            <span className="truncate">{selected.field1}</span>
            {selected.field2 && (
              <span className="ml-2 text-xs text-gray-400 truncate">
                {selected.field2}
              </span>
            )}
          </div>
          <ChevronDownIcon className="h-4 w-4 flex-shrink-0 text-gray-400 ml-2" />
        </button>
        {errors?.[name] && (
          <p className="mt-1 text-xs text-red-600">{errors[name]}</p>
        )}
      </div>

      <PMDialog open={open} onClose={handleClose} size="md" dense closeOnOutsideClick>
        <div className="flex flex-col h-full" style={{ minHeight: 0 }}>
          {/* Header */}
          <div className="flex-shrink-0 p-3 sm:p-4 border-b border-gray-200">
            <div className="flex items-center justify-between mb-3">
              {inCreateView ? (
                <button
                  type="button"
                  onClick={() => setInCreateView(false)}
                  className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
                >
                  <ArrowLeftIcon className="h-4 w-4" />
                  Tilbake
                </button>
              ) : (
                <h3 className="text-base font-semibold text-gray-900">
                  {t(label)}
                </h3>
              )}
              <button
                type="button"
                onClick={handleClose}
                className="rounded-md p-1.5 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            {!inCreateView && (
              <div className="relative">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                  <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
                </div>
                <input
                  ref={searchInputRef}
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Søk etter rapporttype..."
                  autoFocus
                  className="block w-full rounded-md border border-gray-300 bg-white py-2 pl-9 pr-9 text-sm placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
                {query && (
                  <button
                    type="button"
                    onClick={() => setQuery("")}
                    className="absolute inset-y-0 right-0 flex items-center pr-3"
                  >
                    <XMarkIcon className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto" style={{ minHeight: 0 }}>
            {inCreateView ? (
              renderCreateForm()
            ) : loading ? (
              <SelectorListSkeleton />
            ) : (
              <ul className="divide-y divide-gray-100">
                {items.map((item) => {
                  const isSelected = item.id === selected?.id;
                  return (
                    <li
                      key={item.id ?? "none"}
                      onClick={() => handleSelect(item)}
                      className={[
                        "flex items-center justify-between px-4 py-3 cursor-pointer transition-colors",
                        isSelected
                          ? "bg-blue-50 text-blue-700"
                          : "hover:bg-gray-50 text-gray-900",
                      ].join(" ")}
                    >
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{item.primary}</p>
                        {item.secondary && (
                          <p className="text-xs text-gray-500 truncate mt-0.5">
                            {item.secondary}
                          </p>
                        )}
                      </div>
                      {isSelected && (
                        <CheckIcon className="h-4 w-4 flex-shrink-0 ml-3 text-blue-600" />
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* Footer */}
          {!inCreateView && (
            <div className="flex-shrink-0 border-t border-gray-200 p-3">
              <button
                type="button"
                onClick={() => setInCreateView(true)}
                className="flex w-full items-center justify-center gap-2 rounded-md border border-dashed border-gray-300 px-4 py-2.5 text-sm text-gray-600 hover:border-blue-400 hover:text-blue-600 transition-colors"
              >
                <PlusIcon className="h-4 w-4" />
                Opprett ny rapporttype
              </button>
            </div>
          )}
        </div>
      </PMDialog>

      <ServiceReportTypeConditionsDialog
        open={conditionsDialogOpen}
        onClose={() => setConditionsDialogOpen(false)}
        reportType={selected}
      />
    </>
  );
};
